import { z } from 'zod';
import { isoDateTimeSchema, localTimeSchema, uuidSchema } from './common.js';
import { venueFormatAiStreamCodeSchema, venueFormatBpmCurveSchema, } from './venue-format.js';
/**
 * One working-hours window for a single day-of-week.
 */
export const workingHoursEntrySchema = z.object({
    dow: z.number().int().min(0).max(6),
    start: localTimeSchema,
    end: localTimeSchema,
});
/**
 * Venue format code. Backed by `internal.venue_formats.code` — closed
 * dictionary (admin-arch §2.2).
 */
export const venueFormatSchema = z.string().min(1).max(64);
export const venueSchema = z.object({
    id: uuidSchema,
    account_id: uuidSchema,
    name: z.string().min(1).max(255),
    address: z.string().max(1000),
    timezone: z.string().min(1).max(64),
    working_hours: z.array(workingHoursEntrySchema),
    format: venueFormatSchema,
    default_bpm_curve_id: uuidSchema.nullable(),
    created_at: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// Wave 2.13 BE-2 (TASK-072a) — venue card + playback-rules surface
// (admin-prd §6.8, admin-arch §3.7).
// ---------------------------------------------------------------------------
/**
 * IANA timezone string. We do not enumerate the full IANA zone list — instead
 * we shape-check `Area/Location` (or `Area/Region/Location`) which covers
 * every zone the project ships with. Backend never resolves the string at
 * this layer; downstream code uses it as an `Intl.DateTimeFormat` hint.
 */
export const ianaTimezoneSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z][A-Za-z0-9+_-]*\/[A-Za-z0-9+_/-]+$/, 'Expected IANA timezone, e.g. "Europe/Moscow"');
/**
 * Wire DTO for `GET /admin/venues/:id` ("Обзор" — admin-prd §6.8).
 *
 * `format_name` is resolved via JOIN to `internal.venue_formats` so the FE
 * tab renders the human-readable label without a second roundtrip.
 * `account_id` is included so admin nav can wire a "back to account" link.
 */
/**
 * Geo blob for the venue card map widget. Kept loose: only `lat` / `lng`
 * have numeric constraints; `address` is free-form (already covered by the
 * row-level `address` column but bundled here so a future re-geocoding flow
 * can store the resolved postal address alongside the coordinates without a
 * second roundtrip). Added in Wave 2.67 BE polish (TASK-072a-followup).
 */
export const venueGeoSchema = z
    .object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    address: z.string().max(500).optional(),
})
    .strict();
/**
 * Closed venue status enum (Wave 2.67 BE polish, TASK-072a-followup). The DB
 * column accepts any text for forward compatibility, but the application
 * layer hard-narrows to these three values.
 */
export const venueStatusSchema = z.enum(['active', 'paused', 'closed']);
export const venueCardSchema = z.object({
    id: uuidSchema,
    account_id: uuidSchema,
    name: z.string().min(1).max(255),
    format_id: uuidSchema.nullable(),
    format_name: z.string().nullable(),
    timezone: z.string(),
    working_hours: z.array(workingHoursEntrySchema).nullable(),
    status: venueStatusSchema,
    address: z.string().nullable(),
    // Wave 2.67 BE polish (TASK-072a-followup): nullable user metadata.
    code: z.string().max(64).nullable(),
    geo: venueGeoSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
});
/**
 * POST `/admin/venues` body — venue creation (Wave 2.15 BE-1, TASK-076).
 *
 * `subscription_id` is required even though the brief listed only
 * (account_id, name, address, format_id, timezone, working_hours):
 * `customers.venues.subscription_id` is NOT NULL with `ON DELETE RESTRICT`
 * (admin-arch §3.11.3 — `venues → subscription` is the billing pin).
 * Picking the "active" subscription implicitly would be ambiguous when an
 * account has 0 or 2+ active rows; the FE already shows a subscription
 * picker on the create-venue modal (TASK-074b CreateVenueModal) so the
 * field is naturally available.
 *
 * Backend additionally validates that `subscription.account_id == body.account_id`
 * so a stale FE state cannot cross-attach a venue to another tenant's
 * subscription.
 *
 * Timezone is validated via the shape-level `ianaTimezoneSchema` and a
 * runtime `Intl.DateTimeFormat({ timeZone })` resolve in the service.
 *
 * `working_hours` defaults to 9:00-22:00 every day when omitted (PRD
 * convention — admin onboarding presents this as the standard hospitality
 * window; clients edit it later via PATCH). `null` is allowed for "no
 * fixed schedule" semantics.
 */
export const venueCreateSchema = z
    .object({
    account_id: uuidSchema,
    subscription_id: uuidSchema,
    name: z.string().trim().min(1).max(200),
    address: z.string().trim().min(1).max(500),
    format_id: uuidSchema,
    timezone: ianaTimezoneSchema,
    working_hours: z.array(workingHoursEntrySchema).max(14).optional(),
})
    .strict();
/**
 * Response of POST `/admin/venues` — identical to `venueCardSchema` so the
 * caller can drop the new row straight into the venue-card cache.
 */
export const venueCreateResponseSchema = venueCardSchema;
/**
 * PATCH `/admin/venues/:id` body (admin-arch §3.7 — "редактировать
 * имя/адрес/timezone", admin-prd §6.8 "Обзор" field set).
 *
 * `.strict()` rejects unknown keys so a typo (`tz` instead of `timezone`)
 * surfaces as 400 rather than silently dropping. Empty body → 400 via
 * `.refine()` so the caller has unambiguous feedback.
 */
export const venuePatchSchema = z
    .object({
    name: z.string().trim().min(1).max(255).optional(),
    address: z.string().trim().max(1000).nullable().optional(),
    format_id: uuidSchema.nullable().optional(),
    timezone: ianaTimezoneSchema.optional(),
    working_hours: z.array(workingHoursEntrySchema).max(14).nullable().optional(),
    // Wave 2.67 BE polish (TASK-072a-followup) — user metadata.
    code: z.string().trim().max(64).nullable().optional(),
    geo: venueGeoSchema.nullable().optional(),
    status: venueStatusSchema.optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/**
 * Wire DTO for `GET /admin/venues/:id/playback-rules`
 * (admin-prd §4.6.6 + §6.8, admin-arch §3.7).
 *
 * `bpm_curve` reuses the venue-format shape (exactly 24 entries, one per
 * local hour; `bpm_min ≤ bpm_max`). Falls back to the format's default when
 * no per-venue row exists yet — the response shape is identical either way.
 *
 * `anti_*_repeat_seconds` are stored in seconds (PRD §6.8 surfaces them as
 * "минимальный интервал между треками" in minutes; FE converts at the
 * boundary so the wire stays canonical).
 */
export const venuePlaybackRulesSchema = z.object({
    venue_id: uuidSchema,
    bpm_curve: venueFormatBpmCurveSchema,
    anti_artist_repeat_seconds: z.number().int().min(0).max(86400),
    anti_track_repeat_seconds: z.number().int().min(0).max(86400),
    explicit_filter: z.boolean(),
    ai_streams: z.array(venueFormatAiStreamCodeSchema).max(10),
});
/**
 * PATCH `/admin/venues/:id/playback-rules` body. Upsert on first call:
 * the server seeds defaults from `internal.venue_formats` for keys the
 * caller does not override.
 *
 * `.strict()` + non-empty `.refine()` mirror `venuePatchSchema`.
 */
export const venuePlaybackRulesPatchSchema = z
    .object({
    bpm_curve: venueFormatBpmCurveSchema.optional(),
    anti_artist_repeat_seconds: z.number().int().min(0).max(86400).optional(),
    anti_track_repeat_seconds: z.number().int().min(0).max(86400).optional(),
    explicit_filter: z.boolean().optional(),
    ai_streams: z.array(venueFormatAiStreamCodeSchema).max(10).optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
// ---------------------------------------------------------------------------
// Wave 2.27 BE-2 (TASK-082b1) — venue analytics read endpoint
// (admin-arch §3.7 "/admin/venues/:id/analytics", §17 "Venue analytics").
// ---------------------------------------------------------------------------
/**
 * Query string of `GET /admin/venues/:id/analytics`.
 *
 * Both `from` and `to` are optional — the server applies a 30-day default
 * window and clamps the requested window to a 90-day maximum so an admin
 * cannot accidentally scan months of `content.play_events`. The runtime
 * `to > from` ordering check and clamp live in the service so the error
 * carries a human message.
 */
export const venueAnalyticsQuerySchema = z
    .object({
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
})
    .strict();
/**
 * Six fixed BPM buckets covering the catalog (slow → peak). The bounds are
 * inclusive-low / exclusive-high in the SQL aggregator (`>= 80 AND < 100`
 * etc.); `160+` is the open upper bucket.
 */
export const venueAnalyticsBpmBucketSchema = z.enum([
    '0-80',
    '80-100',
    '100-120',
    '120-140',
    '140-160',
    '160+',
]);
/**
 * Closed mood dictionary — mirrors `content.tag_categories` rows seeded by
 * migration 0008 with category code `mood` (admin-arch §2.4 + decisions
 * §35). Unknown moods on the wire (e.g. a brand new tag the FE has not yet
 * shipped a label for) are surfaced as `other` so the chart never breaks.
 */
export const venueAnalyticsMoodSchema = z.enum([
    'energetic',
    'happy',
    'chill',
    'sad',
    'angry',
    'romantic',
    'dreamy',
    'soft',
    'dark',
    'other',
]);
/**
 * Top-N row used by `top_tracks`. `title` falls back to "(удалён)" semantics
 * client-side when the track is soft-deleted — the server passes the original
 * title regardless of `deleted_at` so the FE owns the marker rule.
 */
export const venueAnalyticsTopTrackSchema = z.object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    plays: z.number().int().nonnegative(),
});
/**
 * Top-N artist row. `artist_id` is nullable because `play_events.artist_id`
 * is nullable on the source table (free-form `tracks.artist` text exists
 * without a canonical artist row).
 */
export const venueAnalyticsTopArtistSchema = z.object({
    artist_id: uuidSchema.nullable(),
    artist: z.string(),
    plays: z.number().int().nonnegative(),
});
/**
 * Top-N preset row. Always empty in the current release until
 * `play_events.preset_id_at_play` lands (follow-up
 * `TASK-082b1-followup: preset attribution`).
 */
export const venueAnalyticsPresetUseSchema = z.object({
    preset_id: uuidSchema,
    name: z.string(),
    plays: z.number().int().nonnegative(),
});
/**
 * Wire DTO for `GET /admin/venues/:id/analytics` (admin-arch §17, §3.7).
 *
 * `skip_rate` is OMITTED when the source events lack a skip marker; the
 * current implementation derives it from `play_events.reason_stopped` so
 * the field is always present. Kept optional on the wire so a future
 * feature flag can drop it without breaking the contract.
 *
 * `heatmap` is keyed `[hour_0_23][day_0_6]` (ISO weekday — Monday = 1 in
 * Postgres `EXTRACT(dow)` which returns 0..6 with Sunday = 0; we emit the
 * raw `EXTRACT(dow)` value so the FE labels columns Sun..Sat).
 */
export const venueAnalyticsResponseSchema = z.object({
    window: z.object({
        from: isoDateTimeSchema,
        to: isoDateTimeSchema,
    }),
    totals: z.object({
        plays: z.number().int().nonnegative(),
        unique_tracks: z.number().int().nonnegative(),
        hours_played: z.number().nonnegative(),
        skip_rate: z.number().min(0).max(1).optional(),
    }),
    top_tracks: z.array(venueAnalyticsTopTrackSchema).max(10),
    top_artists: z.array(venueAnalyticsTopArtistSchema).max(10),
    presets_used: z.array(venueAnalyticsPresetUseSchema).max(5),
    mood_distribution: z.record(venueAnalyticsMoodSchema, z.number().int().nonnegative()),
    bpm_histogram: z.array(z.object({
        bucket: venueAnalyticsBpmBucketSchema,
        count: z.number().int().nonnegative(),
    })),
    heatmap: z.record(z.string().regex(/^([0-9]|1[0-9]|2[0-3])$/, 'hour 0..23'), z.record(z.string().regex(/^[0-6]$/, 'dow 0..6'), z.number().int().nonnegative())),
});
// ---------------------------------------------------------------------------
// BUG-278 — venue track feedback (likes / dislikes) surface
// (PRD §9.3 "список лайков и список дизлайков (с возможностью снять лайк или
// дизлайк по запросу клиента)").
//
// GET    /admin/venues/:id/track-feedback?kind=&limit=&offset=
// DELETE /admin/venues/:venueId/track-feedback/:kind/:trackId
//
// The player writes these rows (`customers.track_likes` /
// `customers.track_dislikes`); until now nothing read them back. A dislike
// also removes the track from that venue's rotation (BUG-277), so the removal
// endpoint is what un-blocks a track an operator disliked by mistake.
// ---------------------------------------------------------------------------
export const venueTrackFeedbackKindSchema = z.enum(['like', 'dislike']);
/**
 * Query of `GET /admin/venues/:id/track-feedback`.
 *
 * `kind` is required — likes and dislikes are separate lists in the UI and
 * paginate independently. Page size is capped at 100; the dislike list itself
 * is bounded by the 100-per-venue product cap, likes are not.
 */
export const venueTrackFeedbackQuerySchema = z
    .object({
    kind: venueTrackFeedbackKindSchema,
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
})
    .strict();
/**
 * One feedback row.
 *
 * `reason` is `null` for likes (the player never asks why) and for dislike
 * rows written before the reason modal shipped. It is typed as a free string
 * rather than `playerDislikeReasonSchema` on purpose: the column carries no
 * CHECK constraint, so a value outside today's vocabulary must render as-is
 * instead of failing the whole response — the FE maps known codes to labels.
 *
 * `title` / `artist` come from `content.tracks` and are served even when the
 * track is soft-deleted, so an operator can still explain what was blocked.
 */
export const venueTrackFeedbackEntrySchema = z
    .object({
    track_id: uuidSchema,
    title: z.string(),
    artist: z.string().nullable(),
    reason: z.string().nullable(),
    marked_at: isoDateTimeSchema,
    track_deleted: z.boolean(),
})
    .strict();
export const venueTrackFeedbackResponseSchema = z
    .object({
    kind: venueTrackFeedbackKindSchema,
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    items: z.array(venueTrackFeedbackEntrySchema),
})
    .strict();
// ---------------------------------------------------------------------------
// Wave 2.43 BE-1 (TASK-082b2) — venue analytics exports
// (admin-arch §17 + §3.7 "/admin/venues/:id/analytics/export", PRD §13.8).
//
// POST /admin/venues/:id/analytics/export
//   body: { template_id, from, to, format? }
//   resp: { download_url, expires_at, format, row_count, file_size_bytes }
//
// Window bound differs from the read endpoint above: exports allow up to 365
// days (society reports are quarterly / annual) while interactive analytics
// stays at 90 days for the SLA. Both clamps live in the service so the error
// payload carries a human message and a stable code (`WINDOW_TOO_LARGE`).
//
// `format` overrides the template default. `pdf` is only accepted when the
// template's `format` is `csv` (decision §34 — PDF is a presentation layer on
// top of the CSV layout; xlsx → pdf does not round-trip the column widths).
// xlsx is deferred to a follow-up; requesting it currently returns 501.
// ---------------------------------------------------------------------------
export const venueAnalyticsExportFormatSchema = z.enum(['csv', 'xlsx', 'pdf']);
/**
 * Body of `POST /admin/venues/:id/analytics/export`.
 *
 * `template_id` MUST point at an active `internal.report_templates` row. The
 * server validates `is_active = true`; a soft-deleted template surfaces as
 * 422 `REPORT_TEMPLATE_INACTIVE` so admins re-pick from the dropdown.
 *
 * `from` / `to` are required (unlike the read endpoint): export contracts
 * usually mention an explicit period in the cover page / file name, so a
 * "default last 30 days" default would silently produce the wrong period.
 *
 * `format` is optional — falls back to `template.format` when omitted. `pdf`
 * is accepted regardless of the template format as long as the template's
 * column layout fits the PDF page (current limit: ≤ 8 columns).
 */
export const venueAnalyticsExportRequestSchema = z
    .object({
    template_id: uuidSchema,
    from: isoDateTimeSchema,
    to: isoDateTimeSchema,
    format: venueAnalyticsExportFormatSchema.optional(),
})
    .strict();
/**
 * Response of `POST /admin/venues/:id/analytics/export`.
 *
 * `download_url` is an S3 GET presigned URL valid for 60 seconds — the FE is
 * expected to navigate the browser to it immediately (admin-design §6.14 +
 * FE appendix F-12). `expires_at` is the wall-clock UTC expiry so the FE can
 * surface a "ссылка истекла, повторите" banner if the user clicked away.
 *
 * `row_count` is the number of data rows in the export (excluding header),
 * so the admin sees the volume before downloading. `file_size_bytes` is the
 * exact S3 object size — useful for the email confirmation flow that may
 * land in a later wave.
 */
export const venueAnalyticsExportResponseSchema = z
    .object({
    download_url: z.string().url(),
    expires_at: isoDateTimeSchema,
    format: venueAnalyticsExportFormatSchema,
    row_count: z.number().int().nonnegative(),
    file_size_bytes: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=venue.js.map